import type { SchemaV4Page } from "@cogo/react-renderer/adapters/schema-v4-page";

export type Scenario = "storefront" | "product-detail" | "checkout";

/** A compact summary derived from a schema-v4 page tree (no LLM metadata envelope). */
export interface PageSummary {
  /** the page's blockType, e.g. "storefront-page". */
  blockType: string;
  /** the `type` of each top-level section under `root.children`, in order. */
  sectionTypes: string[];
  /** the total number of elements in the tree (root + all descendants). */
  elementCount: number;
}

interface DemoCardProps {
  scenario: Scenario;
  summary: PageSummary;
  onNavigate: (path: string) => void;
}

/** Recursively count elements in a schema-v4 element subtree. */
function countElements(node: unknown): number {
  if (!node || typeof node !== "object") return 0;
  const children = (node as { children?: unknown[] }).children;
  let count = 1;
  if (Array.isArray(children)) {
    for (const c of children) count += countElements(c);
  }
  return count;
}

/** Derive a compact card summary from a Phase 485 schema-v4 page tree. */
export function summarizePage(page: SchemaV4Page): PageSummary {
  const children = Array.isArray(page.root?.children) ? page.root.children : [];
  return {
    blockType: page.blockType,
    sectionTypes: children.map((c) => String((c as { type?: unknown }).type ?? "?")),
    elementCount: countElements(page.root),
  };
}

const PREVIEW_IMAGES: Record<Scenario, string> = {
  storefront: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&q=80",
  "product-detail": "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&q=80",
  checkout: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&q=80",
};

const SCENARIO_TITLES: Record<Scenario, string> = {
  storefront: "E-Commerce Catalog",
  "product-detail": "Premium Product Detail",
  checkout: "Secure Checkout Gateway",
};

const SCENARIO_DESCS: Record<Scenario, string> = {
  storefront: "A high-fidelity glassmorphic store front featuring vivid oklch dynamic detailing.",
  "product-detail": "An interactive details view with dynamic color pickers and thumbnail selectors.",
  checkout: "A fully validated secure order submission gateway with real-time total calculator.",
};

export function DemoCard({ scenario, summary, onNavigate }: DemoCardProps) {
  return (
    <article
      data-cogo-section="DemoCard"
      data-scenario={scenario}
      className="bg-white/5 border border-white/10 backdrop-blur-md rounded-2xl overflow-hidden shadow-lg hover:border-white/20 transition-all duration-300 transform hover:-translate-y-1.5 flex flex-col h-full font-sans"
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden">
        <img
          src={PREVIEW_IMAGES[scenario]}
          alt={SCENARIO_TITLES[scenario]}
          className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-all duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />
        <span className="absolute top-3 left-3 px-2 py-0.5 bg-indigo-500/80 border border-indigo-400/20 text-[9px] font-bold text-white tracking-widest rounded-full uppercase">
          {summary.blockType.replace("ecommerce-", "")}
        </span>
      </div>

      <div className="p-5 flex-1 flex flex-col justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-white mb-1 font-headline tracking-tight">
            {SCENARIO_TITLES[scenario]}
          </h3>
          <p className="text-xs text-slate-300 leading-relaxed mb-4 font-sans">
            {SCENARIO_DESCS[scenario]}
          </p>

          <div className="flex flex-col gap-2 font-mono text-[10px] text-slate-400 bg-white/5 p-3 rounded-xl border border-white/5">
            <div className="flex justify-between">
              <span>SECTIONS:</span>
              <span className="text-slate-200 text-right truncate max-w-[150px]">
                {summary.sectionTypes.join(", ")}
              </span>
            </div>
            <div className="flex justify-between">
              <span>UI ELEMENTS:</span>
              <span className="text-indigo-400 font-bold">{summary.elementCount} nodes</span>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => onNavigate(`/${scenario}`)}
          className="w-full text-center py-2.5 bg-indigo-500/10 hover:bg-indigo-500 text-indigo-300 hover:text-white border border-indigo-500/30 hover:border-transparent rounded-xl text-xs font-semibold tracking-wide transition-all active:scale-95 cursor-pointer font-headline"
        >
          Enter Live App →
        </button>
      </div>
    </article>
  );
}
